import type { ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ModalProvider from 'contexts/ModalProvider'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import Group from 'components/utils/Group'
import { LOYALTY_LANDING_PAGE, LOYALTY_PURCHASE_POINTS_PAGE, LOYALTY_REWARDS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import TextButton from 'LuxKit/Button/TextButton'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import noop from 'lib/function/noop'
import { SUPPORT_DEFAULT_PHONE_NUMBER, SUPPORT_DEFAULT_PHONE_DISPLAY } from 'constants/support'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { StyledModalHeader } from 'LuxKit/Modal/ModalHeader'
import { ONE_POINT_PROMO_DATE, promoMessages, usePromoValues } from './promoContent'

const PromoModal = styled(Modal)`
  &&& {
    border-radius: ${rem(2)};
  }

  &&& ${StyledModalHeader} {
    align-items: center;
    border-radius: inherit;

    ${mediaQueryUp.tablet} {
      align-items: start;
    }
  }
`
const ModalTitle = styled.span`
  font-size: ${rem(24)};
  line-height: ${rem(32)};
  color: ${props => props.theme.palette.neutral.default.one};
`
const Content = styled(Group)`
  padding-block-end: ${rem(16)};
  color: ${props => props.theme.palette.neutral.default.two};
`
const Introduction = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`
const Steps = styled.ol`
  padding-inline-start: ${rem(20)};
  margin: 0;
`
const SaleEmphasis = styled.strong`
  color: ${props => props.theme.palette.messaging.urgency.normalForeground};
`
const BookingHelpBanner = styled(Group)`
  padding: ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.lightContrast};
`
const phoneIcon = <LinePhoneIcon />
const bookingDateFormat = { day: 'numeric', month: 'long' } as const
const introductionDateFormat = { day: '2-digit', month: 'long', year: 'numeric' } as const
const saleEmphasis = (chunks: ReactNode) => <SaleEmphasis>{chunks}</SaleEmphasis>

const bold = (chunks: ReactNode) => <strong>{chunks}</strong>

function PromoSignupBanner() {
  const intl = useIntl()
  const loggedIn = useAppSelector(selectLoggedIn)
  const { resolve } = useModalElementContext()
  const onNavigate = useCallback(() => resolve(), [resolve])
  const onSignup = usePendingLoginHandler(noop)
  const signup = useCallback((chunks: ReactNode) => (
    <TextLink to={loggedIn ? `/${LOYALTY_LANDING_PAGE}` : undefined} onClick={loggedIn ? onNavigate : onSignup} weight="regular">
      {chunks}
    </TextLink>
  ), [loggedIn, onNavigate, onSignup])
  return <MessageBanner kind="informational">
    {intl.formatMessage({
      id: 'ANm7gbLSPlWsNaQX9yz9',
      defaultMessage: '<bold>Not a Société member?</bold> <signup>Sign up for free</signup>',
      description: 'Signup panel invitation in the Maldives promotion modal, with a link opening the account entry form.',
    }, { bold, signup })}
  </MessageBanner>
}

const plainText = (chunks: ReactNode) => chunks

export default function MaldivesOnePointPromoModal() {
  const intl = useIntl()
  const values = usePromoValues()
  const titleValues = useMemo(() => ({ ...values, em: plainText }), [values])
  const title = <ModalTitle>{intl.formatMessage(promoMessages.title, titleValues)}</ModalTitle>
  const { resolve } = useModalElementContext()
  const onNavigate = useCallback(() => resolve(), [resolve])
  const rewards = useCallback((chunks: ReactNode) => <TextLink to={`/${LOYALTY_REWARDS_PAGE}`} weight="regular" onClick={onNavigate}>{chunks}</TextLink>, [onNavigate])
  const topUp = useCallback((chunks: ReactNode) => <TextLink to={`/${LOYALTY_PURCHASE_POINTS_PAGE}`} weight="regular" onClick={onNavigate}>{chunks}</TextLink>, [onNavigate])
  const activities = useCallback((chunks: ReactNode) => <TextLink to={`/${LOYALTY_LANDING_PAGE}`} weight="regular" onClick={onNavigate}>{chunks}</TextLink>, [onNavigate])
  const pointsBody = useMemo(() => intl.formatMessage({
    id: 'EQT2Y1kdOY5VCG-heyT8',
    defaultMessage: 'You can <topUp>top up your Société Points balance</topUp> and use them straight away, or earn bonus points through eligible <activities>free Société activities.</activities>',
    description: 'Information modal paragraph linking to point purchases and earning activities for the Maldives one-point sale.',
  }, { topUp, activities }), [intl, topUp, activities])
  const rewardsBody = useMemo(() => intl.formatMessage({
    id: 'vZeMVGNPpJ7IA-cZ76VJ',
    defaultMessage: 'Société Rewards Seats are a limited allocation on our direct Melbourne to Maldives service, exclusively reserved for Société members. Redeem your points for the flight, then pay for your choice of hotel package. <rewards>Learn more.</rewards>',
    description: 'Explanation of reward seats and hotel payment in the Maldives one-point promotion information modal.',
  }, { rewards }), [intl, rewards])

  return <PromoModal title={title} size="S" data-testid="maldives-one-point-promo-modal">
    <Content direction="vertical" gap={20}>
      <Introduction variant="medium">
        {intl.formatMessage({
          id: 'wLLIaNYcZz_LxVcSpxmn',
          defaultMessage: 'To celebrate the launch of Société Rewards, we are giving Société members a chance to travel to paradise on our direct flight from Melbourne to Maldives on {date}, for only 1 Société Point + {fare} pp.',
          description: 'Introduction to the Maldives one-point flight promotion with the charter departure date and cash co-payment.',
        }, { ...values, date: intlDateFormatter(ONE_POINT_PROMO_DATE, introductionDateFormat, intl.locale) })}
      </Introduction>
      <ModalProvider><PromoSignupBanner /></ModalProvider>
      <Group direction="vertical" gap={4}>
        <BodyText variant="large" weight="bold" as="h3">
          {intl.formatMessage({
            id: 'UPm_7iFsL2sqCeh9vPT_',
            defaultMessage: 'How it works',
            description: 'Section heading introducing how to book the Maldives one-point charter flight promotion.',
          })}
        </BodyText>
        <Steps>
          <BodyText variant="medium" as="li">
            {intl.formatMessage({
              id: 'os8I2ReYxfZn8KIdFkiv',
              defaultMessage: 'Choose your Maldives hotel and direct flight package.',
              description: 'First booking instruction in the Maldives one-point flight sale information modal.',
            })}
          </BodyText>
          <BodyText variant="medium" as="li">
            {intl.formatMessage({
              id: 'I-jGbAxyG6_BmVm-UeK2',
              defaultMessage: 'Select the <sale>‘One-way flight sale’</sale> from Melbourne to Malé on {date}',
              description: 'Second booking instruction explaining which charter departure to select for the one-point flight sale.',
            }, { sale: saleEmphasis, date: intlDateFormatter(ONE_POINT_PROMO_DATE, bookingDateFormat, intl.locale) })}
          </BodyText>
          <BodyText variant="medium" as="li">
            {intl.formatMessage({
              id: 'Yi2WFBWaOSZYC3KcjBBY',
              defaultMessage: 'At checkout, choose your optional international return flight and any domestic connections you need.',
              description: 'Final booking instruction explaining the optional connecting flights for the one-point charter promotion.',
            })}
          </BodyText>
        </Steps>
      </Group>
      <Group direction="vertical" gap={4}>
        <BodyText variant="large" weight="bold" as="h3">
          {intl.formatMessage({
            id: 'h9SV0l_fvcifAwfIiNW5',
            defaultMessage: 'What are Société Points?',
            description: 'Heading above the explanation of Société Points in the one-point flight promotion modal.',
          })}
        </BodyText>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'YNHmN96Q6Tic0l4uKseG',
            defaultMessage: 'Use your points to pay for all or part of your next escape – simply choose how many to apply at checkout and save instantly.',
            description: 'Explanation of using Société Points at checkout in the Maldives one-point flight promotion modal.',
          })}
        </BodyText>
      </Group>
      <Group direction="vertical" gap={4}>
        <BodyText variant="large" weight="bold" as="h3">
          {intl.formatMessage({
            id: '8mMDY4-XAbsG9_F3UoXc',
            defaultMessage: 'What are Société Reward Seats?',
            description: 'Heading above the definition of Société Reward Seats in the Maldives one-point promotion modal.',
          })}
        </BodyText>
        <BodyText variant="medium">{rewardsBody}</BodyText>
      </Group>
      <Group direction="vertical" gap={4}>
        <BodyText variant="large" weight="bold" as="h3">
          {intl.formatMessage({
            id: 'qEBqnXAHsQXaDm5hpiNG',
            defaultMessage: 'Need Société Points?',
            description: 'Heading above the point top-up and earning links in the Maldives one-point promotion modal.',
          })}
        </BodyText>
        <BodyText variant="medium">{pointsBody}</BodyText>
      </Group>
      <BookingHelpBanner direction="vertical" gap={4}>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'Z8zeJl4Vhu_ajLjCzqZt',
            defaultMessage: 'Need help booking this offer?',
            description: 'Heading for the telephone booking assistance panel in the Maldives one-point promotion modal.',
          })}
        </BodyText>
        <Group direction="vertical" gap={8}>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'ZEnpts9wjCwqMsHg0LsK',
              defaultMessage: 'Speak to a Luxury Escapes Expert anytime, 24 hours, 7 days a week',
              description: 'Description of round-the-clock booking assistance in the Maldives one-point promotion modal.',
            })}
          </BodyText>
          <Group direction="horizontal" horizontalAlign="end">
            <TextButton kind="secondary" size="medium" shape="round" startIcon={phoneIcon} href={`tel:${SUPPORT_DEFAULT_PHONE_NUMBER}`}>
              {intl.formatMessage({
                id: '2GtMSZeVl81wM3yu5h6j',
                defaultMessage: 'Call {phoneNumber}',
                description: 'Call button in the Maldives one-point promotion modal with the booking support telephone number.',
              }, { phoneNumber: SUPPORT_DEFAULT_PHONE_DISPLAY })}
            </TextButton>
          </Group>
        </Group>
      </BookingHelpBanner>
      <TextLink to="/promotions" weight="regular" onClick={onNavigate}>{intl.formatMessage(promoMessages.terms)}</TextLink>
    </Content>
  </PromoModal>
}
